import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Phone, MessageCircle, Clock, AlertCircle, Bell, BellRing, CheckCircle, Trash2, MessageSquareText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import PinAuth from '../components/PinAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminChats = () => {
  const [conversations, setConversations] = useState([]);
  const [filter, setFilter] = useState('pending'); // pending, all, resolved
  const [lastUrgentCount, setLastUrgentCount] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  useEffect(() => {
    const savedPin = sessionStorage.getItem('adminPin');
    if (savedPin) {
      // Verify PIN with backend
      axios.get(`${API}/admin/pin/verify?pin=${savedPin}`)
        .then(response => {
          if (response.data.valid) {
            setIsAuthenticated(true);
          }
        })
        .catch(() => {
          sessionStorage.removeItem('adminPin');
        });
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadConversations();
      
      // Auto-refresh every 5 seconds
      const interval = setInterval(() => {
        loadConversations();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, filter]);

  const loadConversations = async () => {
    try {
      // Fetch from backend API with filter
      const statusFilter = filter === 'all' ? '' : filter === 'pending' ? 'pending' : '';
      const url = statusFilter ? `${API}/chat?status=${statusFilter}` : `${API}/chat`;
      
      const response = await axios.get(url);
      const chats = response.data;
      
      // Only count pending urgent messages
      const urgentChats = chats.filter(c => c.urgency === 'Urgent' && c.status === 'pending');
      
      // Check for new urgent messages
      if (urgentChats.length > lastUrgentCount && lastUrgentCount > 0) {
        // New urgent message received!
        setShowAlert(true);
        
        // Play alert sound (browser notification)
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('🚨 URGENT: New Patient Request!', {
            body: `You have ${urgentChats.length} urgent requests waiting`,
            icon: 'https://customer-assets.emergentagent.com/job_942be312-a7fd-43ba-8969-d3197fe1a24b/artifacts/q27x3yx8_Screenshot%202026-03-26%20233047.png',
            badge: 'https://customer-assets.emergentagent.com/job_942be312-a7fd-43ba-8969-d3197fe1a24b/artifacts/q27x3yx8_Screenshot%202026-03-26%20233047.png',
            vibrate: [200, 100, 200],
            requireInteraction: true
          });
        }
        
        // Show toast notification
        toast.error('🚨 URGENT REQUEST RECEIVED!', {
          description: `New urgent patient request. Total urgent: ${urgentChats.length}`,
          duration: 10000,
        });
        
        // Auto-hide alert after 10 seconds
        setTimeout(() => setShowAlert(false), 10000);
      }
      
      setLastUrgentCount(urgentChats.length);
      setConversations(chats); // Already sorted by backend (newest first)
    } catch (error) {
      console.error('Error loading conversations:', error);
      toast.error('Failed to load messages', {
        description: 'Please refresh the page or check your connection.'
      });
    }
  };

  const handleMarkAsContacted = async (messageId) => {
    try {
      await axios.patch(`${API}/chat/${messageId}/status`, null, {
        params: { status: 'contacted' }
      });
      
      toast.success('Message marked as contacted');
      loadConversations(); // Reload to remove from pending list
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleCall = async (conv) => {
    await handleMarkAsContacted(conv.id);
    window.location.href = `tel:${conv.phone}`;
  };

  const handleWhatsApp = async (conv) => {
    await handleMarkAsContacted(conv.id);
    window.open(`https://wa.me/91${conv.phone}?text=Hi, this is Dard Satya team. Regarding your ${conv.urgency.toLowerCase()} request for ${conv.service}. How can we help you?`, '_blank');
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminPin');
    setIsAuthenticated(false);
  };

  const handleClearAllChats = async () => {
    try {
      const response = await axios.delete(`${API}/chat/clear-all`);
      
      toast.success('All chats cleared!', {
        description: response.data.message
      });
      
      setShowClearConfirm(false);
      loadConversations(); // Reload empty list
    } catch (error) {
      console.error('Error clearing chats:', error);
      toast.error('Failed to clear chats');
    }
  };

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const filteredConversations = conversations.filter(conv => {
    if (filter === 'pending') return conv.status === 'pending';
    if (filter === 'contacted') return conv.status === 'contacted';
    return true; // 'all'
  });

  // PIN Login Screen
  if (!isAuthenticated) {
    return <PinAuth onSuccess={() => setIsAuthenticated(true)} title="Admin Chats" icon={MessageSquareText} />;
  }

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 1000 / 60);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div className="admin-chats-page min-h-screen bg-gradient-to-br from-blue-50 to-white py-12">
      {/* Urgent Alert Banner */}
      {showAlert && conversations.filter(c => c.urgency === 'Urgent').length > 0 && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-bounce">
          <Card className="bg-red-600 text-white border-4 border-red-800 shadow-2xl">
            <CardContent className="p-4 flex items-center gap-3">
              <BellRing size={32} className="animate-ping" />
              <div>
                <p className="font-bold text-xl">🚨 URGENT PATIENT REQUEST!</p>
                <p className="text-red-100">New urgent message received - Please respond immediately</p>
              </div>
              <button onClick={() => setShowAlert(false)} className="ml-4 text-white hover:text-red-200">
                ✕
              </button>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Chat Messages</h1>
              <p className="text-gray-600">Manage incoming chat conversations and requests</p>
            </div>
            <div className="flex items-center gap-3">
              {conversations.filter(c => c.urgency === 'Urgent' && c.status === 'pending').length > 0 && (
                <div className="bg-red-100 border-2 border-red-500 rounded-lg p-3 animate-pulse">
                  <div className="flex items-center gap-2">
                    <Bell className="text-red-600 animate-bounce" size={24} />
                    <div>
                      <p className="text-red-800 font-bold text-lg">
                        {conversations.filter(c => c.urgency === 'Urgent' && c.status === 'pending').length} Urgent Request(s)
                      </p>
                      <p className="text-red-600 text-sm">Requires immediate attention!</p>
                    </div>
                  </div>
                </div>
              )}
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">Total Messages</p>
                  <p className="text-3xl font-bold text-gray-900">{conversations.length}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <MessageCircle className="text-blue-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`border-none shadow-lg ${conversations.filter(c => c.urgency === 'Urgent').length > 0 ? 'ring-4 ring-red-500 animate-pulse' : ''}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1 flex items-center gap-2">
                    Urgent Requests
                    {conversations.filter(c => c.urgency === 'Urgent').length > 0 && (
                      <BellRing size={16} className="text-red-600 animate-bounce" />
                    )}
                  </p>
                  <p className="text-3xl font-bold text-red-600">
                    {conversations.filter(c => c.urgency === 'Urgent').length}
                  </p>
                </div>
                <div className="bg-red-100 p-3 rounded-full">
                  <AlertCircle className="text-red-600" size={24} />
                </div>
              </div>
              {conversations.filter(c => c.urgency === 'Urgent').length > 0 && (
                <p className="text-red-600 text-xs mt-2 font-semibold animate-pulse">⚠️ ACTION REQUIRED!</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">Normal Requests</p>
                  <p className="text-3xl font-bold text-green-600">
                    {conversations.filter(c => c.urgency === 'Normal').length}
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <Clock className="text-green-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6 items-center justify-between">
          <div className="flex gap-3">
            <Button
              onClick={() => setFilter('pending')}
              variant={filter === 'pending' ? 'default' : 'outline'}
              className={filter === 'pending' ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-600 text-blue-600'}
            >
              Pending ({conversations.filter(c => c.status === 'pending').length})
            </Button>
            <Button
              onClick={() => setFilter('all')}
              variant={filter === 'all' ? 'default' : 'outline'}
              className={filter === 'all' ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-600 text-blue-600'}
            >
              All ({conversations.length})
            </Button>
            <Button
              onClick={() => setFilter('contacted')}
              variant={filter === 'contacted' ? 'default' : 'outline'}
              className={filter === 'contacted' ? 'bg-green-600 hover:bg-green-700' : 'border-green-600 text-green-600'}
            >
              Contacted ({conversations.filter(c => c.status === 'contacted').length})
            </Button>
          </div>
          
          {conversations.length > 0 && (
            <Button
              onClick={() => setShowClearConfirm(true)}
              variant="outline"
              className="border-red-600 text-red-600 hover:bg-red-50"
            >
              <Trash2 size={16} className="mr-2" />
              Clear All Chats
            </Button>
          )}
        </div>

        {/* Clear Confirmation Dialog */}
        {showClearConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md border-none shadow-2xl">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Trash2 className="text-red-600" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Clear All Chats?</h3>
                  <p className="text-gray-600">
                    This will permanently delete all {conversations.length} chat messages from the database. This action cannot be undone.
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowClearConfirm(false)}
                    variant="outline"
                    className="flex-1 border-gray-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleClearAllChats}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    Yes, Clear All
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Conversations List */}
        <div className="space-y-4">
          {filteredConversations.length === 0 ? (
            <Card className="border-none shadow-lg">
              <CardContent className="p-12 text-center">
                <MessageCircle size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 text-lg">No chat messages yet</p>
                <p className="text-gray-500 text-sm mt-2">
                  Messages will appear here when customers start chatting
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredConversations.map((conv) => (
              <Card 
                key={conv.id} 
                className={`border-l-4 shadow-md hover:shadow-lg transition-all ${
                  conv.urgency === 'Urgent' 
                    ? 'border-l-red-600 bg-red-50/50 ring-2 ring-red-300 animate-pulse' 
                    : 'border-l-green-600'
                }`}
              >
                <CardContent className="p-6">
                  {conv.urgency === 'Urgent' && (
                    <div className="mb-3 bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 animate-pulse">
                      <BellRing size={20} className="animate-bounce" />
                      <span className="font-bold">🚨 URGENT - IMMEDIATE ACTION REQUIRED!</span>
                    </div>
                  )}
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge 
                          className={`${
                            conv.urgency === 'Urgent' 
                              ? 'bg-red-600 hover:bg-red-700' 
                              : 'bg-green-600 hover:bg-green-700'
                          } text-white`}
                        >
                          {conv.urgency}
                        </Badge>
                        <span className="text-sm text-gray-600 flex items-center gap-1">
                          <Clock size={14} />
                          {formatTimestamp(conv.timestamp)}
                        </span>
                      </div>
                      
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {conv.service}
                      </h3>
                      
                      {conv.phone && (
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded mb-3">
                          <p className="text-sm text-gray-600 mb-1">Customer Phone:</p>
                          <p className="text-lg font-bold text-gray-900">{conv.phone}</p>
                        </div>
                      )}
                      
                      {!conv.phone && (
                        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 rounded mb-3">
                          <p className="text-sm text-yellow-800">📞 Phone number not collected</p>
                        </div>
                      )}
                      
                      <p className="text-gray-700 mb-3 bg-white p-4 rounded-lg border border-gray-200">
                        {conv.message}
                      </p>
                      
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                          {conv.service}
                        </span>
                        <span className="text-xs bg-gray-100 text-gray-800 px-3 py-1 rounded-full">
                          ID: {conv.id}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex md:flex-col gap-2">
                      {conv.phone ? (
                        <>
                          <Button 
                            onClick={() => handleCall(conv)}
                            className="bg-blue-600 hover:bg-blue-700 text-white w-full md:w-auto"
                          >
                            <Phone size={16} className="mr-2" />
                            Call: {conv.phone}
                          </Button>
                          <Button 
                            onClick={() => handleWhatsApp(conv)}
                            variant="outline" 
                            className="border-green-600 text-green-600 hover:bg-green-50 w-full md:w-auto"
                          >
                            <MessageCircle size={16} className="mr-2" />
                            WhatsApp
                          </Button>
                        </>
                      ) : (
                        <div className="text-sm text-gray-600 italic">
                          No contact info available
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Instructions */}
        <Card className="mt-8 border-none shadow-lg bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">📱 How to Access This Page</h3>
            <p className="text-gray-700 mb-4">
              Save this URL to check chat messages from customers. This page auto-refreshes every 5 seconds to show new messages.
            </p>
            <div className="bg-white p-4 rounded-lg border-2 border-blue-300 font-mono text-sm">
              {window.location.origin}/admin/chats
            </div>
            <p className="text-sm text-gray-600 mt-3">
              💡 Tip: Bookmark this page on your phone for quick access to chat notifications
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminChats;
